/**
 * pageNavigationUtils.ts
 *
 * Utilities for page navigation, calculating last answered page, and finding visible pages.
 *
 * Key Functions:
 * - calculateLastAnsweredPage() - Find the highest page index with answered fields
 * - getCurrentVisiblePagePosition() - Get current page's position in visible pages list
 */

import { evaluatePageConditions, findNextVisiblePage, getVisiblePages } from "../../utils/pageConditions";
import { Page, Field } from "../../types/types";

/**
 * Calculate the last answered page index by analyzing which fields have answers.
 * Combines answers from both the current answers Set AND localStorage to ensure we
 * capture all answers even across page navigation and reloads.
 * @param pages - All typed pages
 * @param fields - All typed fields
 * @param answerFieldIds - Set of field IDs that have answers
 * @param localStorageAnswers - Optional answers from localStorage
 * @returns Index of the last page with answered fields, or 0
 */
export const calculateLastAnsweredPage = (
  pages: Page[],
  fields: Field[],
  answerFieldIds: Set<string>,
  localStorageAnswers?: Record<string, any>
): number => {
  // Also check localStorage answers to account for persisted answers
  const allAnswerIds = new Set([
    ...answerFieldIds,
    ...(localStorageAnswers ? Object.keys(localStorageAnswers) : [])
  ]);

  console.log('🔍 [calculateLastAnsweredPage] Checking all available answers:');
  console.log(`   From answers Set: ${answerFieldIds.size} fields`);
  console.log(`   From localStorage: ${localStorageAnswers ? Object.keys(localStorageAnswers).length : 0} fields`);
  console.log(`   Total unique: ${allAnswerIds.size} fields`);

  // Iterate backwards to find the highest page index with answered fields
  for (let i = pages.length - 1; i >= 0; i--) {
    const page = pages[i];
    const pageFields = fields.filter(f => f.pageId === page.id);
    const hasAnsweredField = pageFields.some(field => allAnswerIds.has(field.id));

    if (hasAnsweredField) {
      console.log(`✅ Found answers on page ${i} ("${page.title}"): ${pageFields.filter(f => allAnswerIds.has(f.id)).length} fields`);
      return i;
    }
  }
  return 0; // Default to first page if no answers found
};

/**
 * Get the visible position of a page within the list of visible pages
 * Useful for displaying "Page X of Y" where Y is only visible pages
 * @param pageIndex - Index of the page in full pages array
 * @param visiblePages - Array of visible page indices
 * @returns 1-based position (1 for first visible page, etc.)
 */
export const getVisiblePagePosition = (pageIndex: number, visiblePages: number[]): number => {
  const position = visiblePages.indexOf(pageIndex);
  return position >= 0 ? position + 1 : -1; // Return 1-based position, or -1 if not found
};

/**
 * Check if there's a next visible page from current position
 * @param currentPageIndex - Current page index
 * @param pages - All typed pages
 * @param answersRecord - Current answers
 * @param fields - All typed fields
 * @param direction - Direction to search (1 for next, -1 for previous)
 * @returns True if a visible page exists in that direction
 */
export const hasVisiblePageInDirection = (
  currentPageIndex: number,
  pages: Page[],
  answersRecord: Record<string, any>,
  fields: Field[],
  direction: 1 | -1 = 1
): boolean => {
  return findNextVisiblePage(currentPageIndex, pages, answersRecord, fields, direction) !== null;
};

/**
 * Navigate to page and get metadata about the navigation
 * @param currentPageIndex - Current page index
 * @param pages - All typed pages
 * @param answersRecord - Current answers
 * @param fields - All typed fields
 * @param direction - Direction to navigate (1 for next, -1 for previous)
 * @returns Next page index or null if no visible page found
 */
export const getNextPageIndex = (
  currentPageIndex: number,
  pages: Page[],
  answersRecord: Record<string, any>,
  fields: Field[],
  direction: 1 | -1 = 1
): number | null => {
  return findNextVisiblePage(currentPageIndex, pages, answersRecord, fields, direction);
};

/**
 * Find the first visible page in the form
 * @param pages - All typed pages
 * @param answersRecord - Current answers
 * @param fields - All typed fields
 * @returns Index of first visible page, or 0 if none found
 */
export const getFirstVisiblePageIndex = (
  pages: Page[],
  answersRecord: Record<string, any>,
  fields: Field[]
): number => {
  const firstVisible = findNextVisiblePage(-1, pages, answersRecord, fields, 1);
  return firstVisible !== null ? firstVisible : 0;
};

/**
 * Get the last visible page index
 * @param pages - All typed pages
 * @param answersRecord - Current answers
 * @param fields - All typed fields
 * @returns Index of last visible page, or pages.length - 1 if none found
 */
export const getLastVisiblePageIndex = (
  pages: Page[],
  answersRecord: Record<string, any>,
  fields: Field[]
): number => {
  const lastVisible = findNextVisiblePage(pages.length, pages, answersRecord, fields, -1);
  return lastVisible !== null ? lastVisible : Math.max(0, pages.length - 1);
};

/**
 * Check if a specific page index is valid and visible
 * @param pageIndex - Page index to check
 * @param pages - All typed pages
 * @param answersRecord - Current answers
 * @param fields - All typed fields
 * @returns True if page is visible based on conditions
 */
export const isPageVisible = (
  pageIndex: number,
  pages: Page[],
  answersRecord: Record<string, any>,
  fields: Field[]
): boolean => {
  if (pageIndex < 0 || pageIndex >= pages.length) {
    return false;
  }

  const page = pages[pageIndex];
  return evaluatePageConditions(page, answersRecord, fields);
};
